/**
 * RegistryTab — the "Registry" tab of the Accountability Registry App Pack.
 *
 * Read-only governance projection: for every represented agent in the caller's scope,
 * who is the accountable owner, how material it is, its ownership lifecycle state, and
 * whether the owner's attestation is current. Projection of the enforced ownership
 * records (backend OwnershipRecord → AccountabilityEntry, V336); never writes ownership.
 *
 * Extracted from the Phase-5 AccountabilityRegistryPageV2 so it can live inside the App
 * Pack's tabbed shell (V2PageShell owns the header + TabNav; this renders tab body only).
 * Data: api.listAccountabilityRegistry / api.getOwnershipPosture — DB-paged, RBAC-scoped,
 * names + human labels resolved server-side (no engineering tokens in the UI).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../../services/api';
import ScopeNote from './ScopeNote';
import MaterialityAssessSlideout from './MaterialityAssessSlideout';
import { PostureStrip, PTile, TierBadge, OwnerAvatar, Callout } from './AccountabilityUI';
import {
  Card, Table, Pill, EmptyState, Skeleton, FacetRail, Button, Input,
} from '../../components/v2';
import {
  ShieldCheck, HelpCircle, ChevronLeft, ChevronRight, RefreshCw,
} from 'lucide-react';

const PAGE_SIZE = 20;

const STATE_FACETS = [
  { key: 'ACTIVE', label: 'Accountable owner' },
  { key: 'NOMINATED', label: 'Awaiting acceptance' },
  { key: 'DISCOVERED_UNOWNED', label: 'Discovered — unowned' },
  { key: 'ORPHANED', label: 'Orphaned' },
  { key: 'TRANSFER_PENDING', label: 'Transfer in progress' },
  { key: 'RETIRED', label: 'Retired' },
];
const TIER_FACETS = [
  { key: 'TIER_1', label: 'Critical' },
  { key: 'TIER_2', label: 'Significant' },
  { key: 'TIER_3', label: 'Limited' },
  { key: 'TIER_4', label: 'Immaterial' },
];
const ATTESTATION_FACETS = [
  { key: 'CURRENT', label: 'Attested' },
  { key: 'STALE', label: 'Re-attestation due' },
  { key: 'LAPSED', label: 'Re-attestation overdue' },
  { key: 'NONE', label: 'Not attested' },
];

function stateTone(state) {
  switch (state) {
    case 'ACTIVE': return 'success';
    case 'NOMINATED': return 'warning';
    case 'DELEGATED': return 'brand';
    case 'TRANSFER_PENDING':
    case 'SUCCESSION_PENDING': return 'action';
    case 'ORPHANED':
    case 'DISCOVERED_UNOWNED': return 'danger';
    case 'RETIRED': return 'muted';
    default: return 'muted';
  }
}
function attestationTone(status) {
  switch (status) {
    case 'CURRENT': return 'success';
    case 'STALE': return 'warning';
    case 'LAPSED': return 'danger';
    default: return 'muted';
  }
}
function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (e) {
    return '—';
  }
}

export default function RegistryTab({ active = true, customerId = null }) {
  const navigate = useNavigate();

  // /ownership/rollup is no longer called here. Once every KPI came from the posture projection
  // this was a second round-trip whose only remaining job was gating a skeleton — and keeping it
  // meant keeping a second population correct forever for no benefit. Deleting the call is the
  // durable fix; a source nobody reads cannot drift back into the strip.
  const [estateLoading, setEstateLoading] = useState(true);
  const [estate, setEstate] = useState(null); // examiner-based posture (independence + attestation)
  // A failed read and a clean estate are NOT the same picture. Without this the strip showed
  // "0 violations · — attestation" whenever the call errored, which is the single most misleading
  // thing this surface can display.
  const [estateFailed, setEstateFailed] = useState(false);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [stateFilter, setStateFilter] = useState(null);
  const [tierFilter, setTierFilter] = useState(null);
  const [attestationFilter, setAttestationFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [assessAgent, setAssessAgent] = useState(null); // RC-OWN/P0 — materiality assessment slideout target

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [stateFilter, tierFilter, attestationFilter, debouncedSearch]);

  // Approved posture set — reuses the examiner service's live aggregates (independence +
  // attestation) WITHOUT the exceptions scan, via the lightweight /ownership/posture endpoint.
  const loadEstate = useCallback(async () => {
    setEstateLoading(true);
    try {
      const res = await api.getOwnershipPosture(customerId);
      const data = res?.data ?? res;
      setEstate(data || null);
      setEstateFailed(false);
    } catch (e) {
      setEstate(null);
      setEstateFailed(true);
    } finally {
      setEstateLoading(false);
    }
    // customerId is a real dependency: without it this memoised loader keeps the customer that was
    // current when it was created, and switching customers would silently show stale scope.
  }, [customerId]);

  const loadRegistry = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listAccountabilityRegistry({
        state: stateFilter,
        tier: tierFilter,
        attestationStatus: attestationFilter,
        q: debouncedSearch || undefined,
        customerId: customerId || undefined,
        page,
        size: PAGE_SIZE,
      });
      // apiFetch wraps success as { ok, data, etag }; the Page body is under .data.
      const data = res?.data ?? res;
      setRows(Array.isArray(data?.content) ? data.content : []);
      setTotal(typeof data?.totalElements === 'number' ? data.totalElements : 0);
    } catch (e) {
      setError(e?.message || 'Could not load the Accountability Registry.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
    // customerId belongs in these deps for the same reason as the loaders above — and because the
    // table and the KPI strip must never be looking at two different customers at once.
  }, [stateFilter, tierFilter, attestationFilter, debouncedSearch, page, customerId]);

  useEffect(() => { loadEstate(); }, [loadEstate]);
  useEffect(() => { loadRegistry(); }, [loadRegistry]);

  // Background refresh on re-activation (keep-alive keeps this tab mounted).
  // Skips the first activation — the mount effects above already loaded — and
  // shows no skeleton on refresh (data is retained), so switching never flashes.
  const reactivatedRef = React.useRef(false);
  useEffect(() => {
    if (!active) return;
    if (!reactivatedRef.current) { reactivatedRef.current = true; return; }
    loadEstate();
    loadRegistry();
  }, [active]);

  const facetGroups = useMemo(() => ([
    { key: 'state', label: 'Lifecycle', options: STATE_FACETS.map((f) => ({ key: f.key, label: f.label, checked: stateFilter === f.key })) },
    { key: 'tier', label: 'Materiality', options: TIER_FACETS.map((f) => ({ key: f.key, label: f.label, checked: tierFilter === f.key })) },
    { key: 'attestation', label: 'Attestation', options: ATTESTATION_FACETS.map((f) => ({ key: f.key, label: f.label, checked: attestationFilter === f.key })) },
  ]), [stateFilter, tierFilter, attestationFilter]);

  const onFacetToggle = useCallback((groupKey, optionKey, nextChecked) => {
    const next = nextChecked ? optionKey : null;
    if (groupKey === 'state') setStateFilter(next);
    else if (groupKey === 'tier') setTierFilter(next);
    else if (groupKey === 'attestation') setAttestationFilter(next);
  }, []);

  // ALL FOUR KPIs now come from /ownership/posture — the 1-row-per-agent registry projection that
  // this page's own table is built from.
  //
  // They used to be split across two SOURCES, not just two queries: "Represented agents" and
  // "Critical or significant" were folded from /ownership/rollup, which aggregates the
  // ownership_records LEDGER, while "Independence violations" and "Attestation current" came from
  // the accountability_entries PROJECTION. The ledger holds one row per ownership GENERATION — a
  // nomination that lapses is retired and a fresh record minted, so one agent can own several —
  // and it keeps records for agents that were deleted outright. The projection holds exactly one
  // row per agent (uq_accountability_entry_agent).
  //
  // On the live estate that read as "30 REPRESENTED AGENTS" beside "12 of 34 current": two
  // denominators, one strip, neither labelled. Subtracting RETIRED from the ledger total was an
  // attempt to reconcile them that cannot work — it also removes agents whose only record is
  // retired but which still have an entry, so the two counts diverge in both directions.
  //
  // LifecycleTab already reached this conclusion and switched to the projection, saying so in its
  // own comment ("the record ledger double-counts generations and retains retired records of
  // fully-deleted agents, which is confusing here"). The fix was written down one file over and
  // never applied here. One source, the same one the table below uses.
  const posture = useMemo(() => {
    const byState = estate?.inventory?.byState || {};
    const byTier = estate?.materiality?.byTier || {};
    return {
      total: estate?.inventory?.represented ?? 0,
      accountable: byState.ACTIVE ?? 0,
      awaiting: (byState.NOMINATED ?? 0) + (byState.DISCOVERED_UNOWNED ?? 0),
      critical: (byTier.TIER_1 ?? 0) + (byTier.TIER_2 ?? 0),
      unscored: estate?.materiality?.unscored ?? 0,
    };
  }, [estate]);

  // Approved posture (independence violations + attestation-current %) from /ownership/posture.
  const estatePosture = useMemo(() => {
    const ind = estate?.independence || {};
    const att = estate?.attestation || {};
    const attSum = (att.current || 0) + (att.stale || 0) + (att.lapsed || 0) + (att.none || 0);
    const violations = (ind.selfOwned || 0) + (ind.ownerless || 0);
    return {
      attCurrent: att.current || 0,
      attSum,
      attCurrentPct: attSum > 0 ? Math.round(((att.current || 0) / attSum) * 100) : null,
      violations,
      selfOwned: ind.selfOwned || 0,
      ownerless: ind.ownerless || 0,
    };
  }, [estate]);

  const columns = useMemo(() => ([
    { key: 'agentName', header: 'Agent', render: (r) => <span className="font-medium text-v2-ink">{r.agentName || 'Unnamed agent'}</span> },
    {
      key: 'ownerName', header: 'Accountable owner',
      // The accountable owner exists only once ACCEPTED (ACTIVE). A NOMINATED record carries a
      // proposed owner pending their acceptance; everything else is genuinely unassigned. We never
      // present a pre-acceptance nominee as the accountable owner (that was the old confusion).
      render: (r) => {
        if (r.ownershipState === 'ACTIVE' && r.ownerName) {
          return (
            <span className="inline-flex items-center gap-2">
              <OwnerAvatar name={r.ownerName} />
              <span className="text-v2-ink">{r.ownerName}</span>
            </span>
          );
        }
        if (r.ownershipState === 'NOMINATED' && r.ownerName) {
          return (
            <span className="inline-flex items-center gap-2">
              <OwnerAvatar name={r.ownerName} />
              <span className="text-v2-ink-muted">Pending · {r.ownerName}</span>
            </span>
          );
        }
        return <span className="text-v2-ink-muted">— unassigned</span>;
      },
    },
    {
      key: 'creatorName', header: 'Onboarded by',
      // Provenance — who created/onboarded the system (already resolved server-side on the row).
      render: (r) => (r.creatorName
        ? <span className="text-v2-ink-secondary">{r.creatorName}</span>
        : <span className="text-v2-ink-muted">—</span>),
    },
    {
      key: 'materiality', header: 'Materiality',
      // P0 — the materiality cell is the assessment affordance: click to open the rubric form
      // (stopPropagation so it doesn't trigger the row's navigate). Backend RBAC gates the write.
      render: (r) => (
        <button
          type="button"
          className="v2-focus-ring inline-flex items-center rounded-v2-pill hover:opacity-80"
          title={r.materialityTier ? 'Re-assess materiality' : 'Assess materiality'}
          onClick={(e) => { e.stopPropagation(); setAssessAgent({ agentId: r.agentId, agentName: r.agentName }); }}
        >
          {r.materialityTier
            ? <TierBadge tier={r.materialityTier} label={r.materialityLabel} />
            : <span className="text-v2-action text-[12px] font-medium">Score →</span>}
        </button>
      ),
    },
    { key: 'ownershipState', header: 'Lifecycle', render: (r) => <Pill tone={stateTone(r.ownershipState)} dot>{r.ownershipStateLabel}</Pill> },
    { key: 'attestation', header: 'Attestation', render: (r) => <Pill tone={attestationTone(r.attestationStatus)}>{r.attestationLabel}</Pill> },
    { key: 'lastProjectedAt', header: 'Updated', align: 'right', render: (r) => <span className="text-v2-ink-muted">{formatWhen(r.lastProjectedAt)}</span> },
  ]), []);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const anyFilter = stateFilter || tierFilter || attestationFilter || debouncedSearch;
  const clearFilters = () => {
    setStateFilter(null);
    setTierFilter(null);
    setAttestationFilter(null);
    setSearch('');
  };

  return (
    <>
      <ScopeNote scope={estate?.scope} className="mb-2" />
      {estateFailed && (
        <div className="mb-2 text-[12.5px] text-v2-danger">
          We could not read the ownership figures for this customer, so the numbers below are
          incomplete — treat them as unknown rather than as a clean result. Use Refresh to try
          again.
        </div>
      )}
      <div className="flex items-center justify-end mb-3">
        <Button
          variant="secondary"
          size="sm"
          iconLeft={<RefreshCw size={15} />}
          onClick={() => { loadEstate(); loadRegistry(); }}
        >
          Refresh
        </Button>
      </div>

      {/* Posture strip — approved-mockup compact pills, real data only */}
      {estateLoading && !estate ? (
        <div className="flex flex-wrap gap-2.5 mb-4">
          <Skeleton variant="rect" height={44} width={150} />
          <Skeleton variant="rect" height={44} width={170} />
          <Skeleton variant="rect" height={44} width={190} />
          <Skeleton variant="rect" height={44} width={160} />
        </div>
      ) : (
        <PostureStrip>
          <PTile
            value={estatePosture.violations}
            label="Independence violations"
            tone={estatePosture.violations > 0 ? 'crit' : 'good'}
            sub={`${estatePosture.selfOwned} self-owned · ${estatePosture.ownerless} ownerless`}
          />
          <PTile
            value={estatePosture.attCurrentPct == null ? '—' : `${estatePosture.attCurrentPct}%`}
            label="Attestation current"
            tone={estatePosture.attCurrentPct != null && estatePosture.attCurrentPct >= 90 ? 'good' : 'default'}
            sub={estatePosture.attSum > 0 ? `${estatePosture.attCurrent} of ${estatePosture.attSum} current` : null}
          />
          <PTile value={posture.total} label="Represented agents" />
          <PTile
            value={posture.critical}
            label="Critical or significant"
            tone={posture.critical > 0 ? 'crit' : 'default'}
          />
        </PostureStrip>
      )}

      {/* Real-data callout: unowned systems are a first-class, visible state */}
      {!estateLoading && estatePosture.ownerless > 0 ? (
        <Callout tone="warning" className="mb-4">
          <b className="text-v2-ink">{estatePosture.ownerless} system{estatePosture.ownerless === 1 ? '' : 's'} with no accountable owner.</b>{' '}
          Ownerless is a first-class, visible state — surfaced here, aging, and assignable via claim + attestation.
        </Callout>
      ) : null}

      <div className="flex gap-4 items-start">
        <div className="hidden lg:block w-60 shrink-0">
          <FacetRail groups={facetGroups} onToggle={onFacetToggle} />
          {anyFilter ? (
            <div className="mt-3">
              <Button variant="ghost" size="sm" onClick={clearFilters} fullWidth>Clear filters</Button>
            </div>
          ) : null}
        </div>

        <div className="flex-1 min-w-0">
          <div className="mb-3 max-w-sm">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search agents by name"
              aria-label="Search agents by name"
            />
          </div>

          <Card padding="none">
            {loading && rows.length === 0 ? (
              <div className="p-4"><Skeleton variant="text" lines={8} /></div>
            ) : error ? (
              <EmptyState
                icon={<HelpCircle size={22} />}
                title="Could not load the registry"
                description={error}
                primaryAction={<Button variant="secondary" size="sm" onClick={loadRegistry}>Try again</Button>}
              />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<ShieldCheck size={22} />}
                title={anyFilter ? 'No agents match these filters' : 'No agents represented yet'}
                description={anyFilter
                  ? 'Adjust or clear the filters to see more of the estate.'
                  : 'Once ownership representation is enabled, every agent appears here with its accountable owner.'}
                primaryAction={anyFilter
                  ? <Button variant="secondary" size="sm" onClick={clearFilters}>Clear filters</Button>
                  : undefined}
              />
            ) : (
              <Table
                columns={columns}
                rows={rows}
                rowKey={(r) => r.entryId}
                onRowClick={(r) => navigate(`/agents/${r.agentId}`)}
                ariaLabel="Accountability Registry"
              />
            )}
          </Card>

          {!loading && !error && total > PAGE_SIZE ? (
            <div className="flex items-center justify-between mt-3">
              <span className="text-[13px] text-v2-ink-muted">
                {`Page ${page + 1} of ${pageCount} · ${total} agents`}
              </span>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" iconLeft={<ChevronLeft size={15} />} disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
                <Button variant="ghost" size="sm" iconRight={<ChevronRight size={15} />} disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          ) : null}
        </div>
        <MaterialityAssessSlideout
          agent={assessAgent}
          onClose={() => setAssessAgent(null)}
          onScored={() => { setAssessAgent(null); loadRegistry(); loadEstate(); }}
        />
      </div>
    </>
  );
}
